import {
    TNormalizedService,
    TServiceNavigationSection,
} from '../../types/serviceView'
import { firstNonEmpty, isRecord } from './common'

const RESERVED_SERVICE_GROUP_SLUGS = new Set(['dns'])

const encodeServiceId = (id: string): string => encodeURIComponent(id)

const slugify = (value: string): string =>
    value
        .toLowerCase()
        .trim()
        .replace(/[^a-z0-9]+/g, '-')
        .replace(/^-+|-+$/g, '')

const normalizeGroupValue = (value: unknown): number | string | undefined => {
    if (typeof value === 'number' || typeof value === 'string') {
        return value
    }

    return undefined
}

const groupsMatch = (
    left?: number | string,
    right?: number | string
): boolean => {
    if (left === undefined || right === undefined) {
        return false
    }

    return String(left) === String(right)
}

export const buildServiceGroupSlug = (
    name: string,
    id?: number,
    usedSlugs?: Set<string>
): string => {
    const fallbackId = id ?? 0
    const baseSlug = slugify(name) || `group-${fallbackId}`
    const preferredSlug = RESERVED_SERVICE_GROUP_SLUGS.has(baseSlug)
        ? `${baseSlug}-service`
        : baseSlug

    if (!usedSlugs) {
        return preferredSlug
    }

    if (!usedSlugs.has(preferredSlug)) {
        usedSlugs.add(preferredSlug)
        return preferredSlug
    }

    const dedupedSlug = RESERVED_SERVICE_GROUP_SLUGS.has(baseSlug)
        ? `${baseSlug}-${fallbackId}-service`
        : `${baseSlug}-${fallbackId}`

    usedSlugs.add(dedupedSlug)
    return dedupedSlug
}

export const buildServiceDetailPath = (
    productId: string
): string => `/services/${encodeServiceId(productId)}`

export const buildServiceGroupFilterPath = (sectionId?: string): string =>
    sectionId && sectionId !== 'all'
        ? `/services?group=${encodeURIComponent(sectionId)}`
        : '/services'

const buildAvailableGroupIds = (services: unknown[]): Set<string> => {
    const groupIds = new Set<string>()

    services.forEach((service) => {
        const rawService = isRecord(service) ? service : {}
        const group = normalizeGroupValue(rawService.group)
        if (group !== undefined) {
            groupIds.add(String(group))
        }
    })

    return groupIds
}

type TNormalizeServiceOptions = {
    groupName?: string
}

export const buildServiceNavigationPath = (
    rawService: unknown
): string => {
    const raw = isRecord(rawService) ? rawService : {}
    const id = firstNonEmpty(raw.id, raw.productCode)

    if (!id) {
        return ''
    }

    return buildServiceDetailPath(id)
}

export const resolveServiceGroupName = (
    group: number | string | undefined,
    groups: Array<{ id: number | string; name: string }> = [],
    fallback?: string
): string | undefined => {
    if (fallback) {
        return fallback
    }

    if (group === undefined) {
        return undefined
    }

    return groups.find(
        (serviceGroup) => String(serviceGroup.id) === String(group)
    )?.name
}

export const normalizeService = (
    rawService: unknown,
    options: TNormalizeServiceOptions = {}
): TNormalizedService => {
    const raw = isRecord(rawService) ? rawService : {}
    const id = firstNonEmpty(raw.id, raw.productCode)
    const name = firstNonEmpty(raw.name, 'Unnamed service')
    const type = firstNonEmpty(raw.type, 'Service')
    const productCode = firstNonEmpty(raw.productCode, id)
    const orderNumber = firstNonEmpty(raw.orderNumber) || undefined
    const goLiveDate = firstNonEmpty(raw.goLiveDate) || undefined
    const group = normalizeGroupValue(raw.group)
    const groupName = resolveServiceGroupName(
        group,
        [],
        options.groupName || firstNonEmpty(raw.groupName) || undefined
    )

    return {
        id,
        name,
        type,
        productCode,
        detailPath: buildServiceDetailPath(id),
        ...(orderNumber ? { orderNumber } : {}),
        ...(goLiveDate ? { goLiveDate } : {}),
        ...(group !== undefined ? { group } : {}),
        ...(groupName ? { groupName } : {}),
    }
}

export const buildFallbackServiceNavigationSections = (
    serviceGroups: Array<{ id: number; name: string }>,
    services: unknown[] = []
): TServiceNavigationSection[] => {
    const sections: TServiceNavigationSection[] = [
        { id: 'all', label: 'All Services', path: '/services' },
    ]
    const availableGroupIds = buildAvailableGroupIds(services)
    const usedSlugs = new Set<string>()

    serviceGroups.forEach((group) => {
        if (!group?.name || !availableGroupIds.has(String(group.id))) {
            return
        }

        const slug = buildServiceGroupSlug(group.name, group.id, usedSlugs)
        sections.push({
            id: slug,
            label: group.name,
            path: buildServiceGroupFilterPath(slug),
            groupId: group.id,
        })
    })

    return sections
}

export const serviceMatchesSection = (
    service: TNormalizedService,
    section: TServiceNavigationSection | null
): boolean => {
    if (!section || section.id === 'all') {
        return true
    }

    if (section.groupId === undefined) {
        return false
    }

    return groupsMatch(service.group, section.groupId)
}
